/**
 * BarcodeScannerView — Full-screen barcode scanning view with its own
 * top overlay (title + hint), camera feed, and bottom controls
 * (mode toggle + manual entry).
 */

import React, { useState, useCallback} from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Loader from "@amzn/meridian/loader";
import BarcodeScanner from "src/components/pages/barcodeScanner/BarcodeScanner";
import ManualEntryPage from "./ManualEntryPage";
import ClassificationProgressScreen from "./ClassificationProgressScreen";
import { ScanModeToggle, ScanMode, ScannerTopOverlay, SDS_INSIGHT_MESSAGE } from "./ScannerSharedComponents";
import { useClassifyItem } from "./useClassifyItem";
import { ClassificationType } from "src/types/classificationTypes";
import PillButton from "src/components/UI/PillButton";
import "src/components/UI/cameraOverlay.scss";
import "./classification.scss";
import { Button, Icon, Sheet } from "@amzn/meridian";
import searchTokens from "@amzn/meridian-tokens/base/icon/search";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";

interface BarcodeScannerViewProps {
  activeMode: ScanMode;
  onModeChange: (mode: ScanMode) => void;
}

const BarcodeScannerView: React.FC<BarcodeScannerViewProps> = ({ activeMode, onModeChange }) => {
  const { classify, isLoading, isAnalyzing, productInfo } = useClassifyItem();
  const [isScanning, setIsScanning] = useState(true);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const handleScan = useCallback(
    async (result: any) => {
      if (isLoading || !isScanning) return;
      if (!result || result.length === 0) return;

      const asin = String(result[0].rawValue).trim();
      if (!asin) return;
      setScanError(null);
      setIsScanning(false);
      try {
        await classify(asin);
      } catch {
        setScanError("We couldn't identify this product. Make sure the barcode is clear or try another product.");
        setIsScanning(true);
      }
    },
    [isLoading, isScanning, classify]
  );

  if (isAnalyzing) {
    return (
      <ClassificationProgressScreen
        classificationConfig={{
          type: ClassificationType.SDS,
          imageUrl: productInfo?.imageUrl || "",
          productName: productInfo?.productName || "Product Name",
          asin: productInfo?.asin || "—",
          aiInsightMessage: SDS_INSIGHT_MESSAGE,
        }}
      />
    );
  }

  if (showManualEntry) {
    return <ManualEntryPage onSwitchToScanner={() => setShowManualEntry(false) } />;
  }

  return (
    <Column className="barcode-scanner-viewport pwa-fullscreen-viewport" backgroundColor="#3a3a3a" height="100vh" spacing="none">

      {/* Top overlay */}
      <ScannerTopOverlay title="Waste Profile Classification">
        <Row className="camera-pill-bg" width="300px" alignmentHorizontal="center" spacingInset="300">
          <Text color="inverted">Scan the product barcode</Text>
        </Row>
      </ScannerTopOverlay>

      {/* Camera feed */}
      <BarcodeScanner
        isScanning={isScanning}
        onScan={handleScan}
        onError={(err) => console.error("[BarcodeScanner] Error:", err)}
      />

      {/* Bottom overlay */}
      <Column className="camera-overlay camera-overlay--bottom" spacingInset="450" alignmentHorizontal="center" spacing="400">
        <ScanModeToggle activeMode={activeMode} onModeChange={onModeChange} />
        <PillButton text="Having Trouble? Try manual Entry" color="glass" onClick={() => setShowManualEntry(true)} />
      </Column>

      {/* Scan error bottom sheet */}
      <Sheet type="overlay" side="bottom" open={scanError !== null || isLoading} onClose={() => {setScanError(null)}} spacingInset="400">
        <Column spacingInset="400" alignmentHorizontal="center" spacing="none">

          {isLoading ?
            (
              <Row spacingInset="600" alignmentHorizontal="center" alignmentVertical="center">
                <Loader size="large" type="circular" />
                <Text>Fetching classification data...</Text>
              </Row>
            
            ) : (
              <>
                <Row alignmentHorizontal="end" width="100%">
                  <Button onClick={() => setScanError(null)} type="icon">
                    <Icon tokens={closeSmallTokens} color="primary" />
                  </Button>
                </Row>
                <Column alignmentHorizontal="center" spacing="500">
                  <Icon tokens={searchTokens} />
                  <Text type="h300" alignment="center">Product not recognized.</Text>
                  <Text type="b400" color="secondary" alignment="center">{scanError}</Text>
                  <PillButton text="SCAN ANOTHER PRODUCT / BARCODE" color="secondary" onClick={() => setScanError(null)} />
                </Column>
              </>
            )}
        </Column>

      </Sheet>
    </Column>
  );
};

export default BarcodeScannerView;
